import { useForm } from '@tanstack/react-form';
import { MHMNumberInput } from '../MHM/MHMNumberInput';
import {
  maxValue,
  minValue,
  nonEmpty,
  object,
  pipe,
  string,
  toNumber,
} from 'valibot';
import { useRecoilValue } from 'recoil';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { useEffect } from 'react';
import {
  MIN_WARNING_PERCENTAGE,
  MAX_WARNING_PERCENTAGE,
} from '../../../hooks/useWarningLevel';

const percentageSchema = pipe(
  string(),
  nonEmpty('Please provide a valid number!'),
  toNumber('Please provide a valid number!'),
  minValue(MIN_WARNING_PERCENTAGE, 'Not in the allowable range percentage'),
  maxValue(MAX_WARNING_PERCENTAGE, 'Not in the allowable range percentage'),
);

type Props = {
  canEditSession: boolean;
  defaultWarningPercentage: number;
  onChange(
    percentage: number,
    errorMessage: [string, Record<string, string>],
  ): void;
};

export function DeviceWarningLevelPercentage({
  canEditSession,
  defaultWarningPercentage,
  onChange,
}: Props) {
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const form = useForm({
    defaultValues: {
      warningPercentage: String(
        Math.round(defaultWarningPercentage * 100) / 100,
      ),
    },
    validators: { onChange: object({ warningPercentage: percentageSchema }) },
    listeners: {
      onChangeDebounceMs: 1,
      onChange({ fieldApi }) {
        const errorMessage =
          fieldApi.state.meta.errorMap.onChange?.[0].message ?? '';
        onChange(Number(fieldApi.state.value), [
          errorMessage,
          {
            minPercentage: String(MIN_WARNING_PERCENTAGE),
            maxPercentage: String(MAX_WARNING_PERCENTAGE),
          },
        ]);
      },
    },
  });

  useEffect(() => {
    form.reset();
  }, [selectedDevice]);

  return (
    <form.Field name="warningPercentage">
      {field => {
        return (
          <MHMNumberInput
            allowDecimal
            value={field.state.value}
            onChange={e => field.handleChange(e.target.value)}
            disabled={!canEditSession}
            slotProps={{
              input: { endAdornment: '%' },
              htmlInput: { sx: { minWidth: '3rem' } },
            }}
            error={!field.state.meta.isValid}
            sx={{ width: 'min-content' }}
            autoComplete="off"
          />
        );
      }}
    </form.Field>
  );
}
